import type { ComponentInstance } from '../component.js';
import { interactiveButton, setHtmlIcon, setText } from '../dom-helpers.js';
import { ICON_WARN } from '../icons.js';

export interface ErrorTransientProps {
  reason: string;
  attempt: number;
  canRetry: boolean;
  embed: boolean;
}

export interface ErrorTransientCallbacks {
  onRetry: () => void;
}

export function mountErrorTransient(
  root: HTMLElement,
  initial: ErrorTransientProps,
  cb: ErrorTransientCallbacks,
): ComponentInstance<ErrorTransientProps> {
  root.innerHTML = '';

  const panel = document.createElement('div');
  panel.classList.add('viewer-ui-interactive');
  panel.setAttribute('role', 'alert');
  panel.style.cssText = initial.embed
    ? 'position:fixed;bottom:1rem;left:50%;transform:translateX(-50%);background:rgba(15,15,17,0.92);backdrop-filter:blur(8px);color:#f4f4f5;padding:0.875rem 1.125rem;border-radius:0.5rem;max-width:24rem;font-family:system-ui,sans-serif;font-size:0.875rem;display:flex;align-items:flex-start;gap:0.75rem;pointer-events:auto'
    : 'position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(9,9,11,0.92);color:#f4f4f5;font-family:system-ui,sans-serif;pointer-events:auto';

  const inner = document.createElement('div');
  inner.style.cssText = initial.embed
    ? 'display:flex;align-items:flex-start;gap:0.75rem'
    : 'max-width:32rem;text-align:center;padding:2rem;display:flex;flex-direction:column;align-items:center;gap:1rem';

  const iconEl = document.createElement('div');
  iconEl.setAttribute('aria-hidden', 'true');
  setHtmlIcon(iconEl, ICON_WARN);
  inner.appendChild(iconEl);

  const textWrap = document.createElement('div');
  textWrap.style.cssText = 'display:flex;flex-direction:column;gap:0.5rem;max-width:24rem';
  inner.appendChild(textWrap);

  const heading = document.createElement('p');
  heading.style.cssText = initial.embed ? 'font-weight:500' : 'font-size:1.25rem;font-weight:600';
  setText(heading, 'Errore temporaneo');
  textWrap.appendChild(heading);

  const body = document.createElement('p');
  body.style.cssText = 'color:#a1a1aa;line-height:1.5';
  textWrap.appendChild(body);

  const actions = document.createElement('div');
  actions.style.cssText = 'margin-top:0.5rem;display:flex;gap:0.5rem;align-items:center';
  textWrap.appendChild(actions);

  let retryBtn: HTMLButtonElement | null = null;

  const update = (props: ErrorTransientProps): void => {
    setText(
      body,
      props.canRetry
        ? `Si è verificato un problema (${props.reason}). Tentativo n. ${props.attempt}.`
        : `Si è verificato un problema (${props.reason}). Ricarica la pagina per riprovare.`,
    );

    if (props.canRetry) {
      if (retryBtn === null) {
        retryBtn = interactiveButton('Riprova', cb.onRetry);
        retryBtn.style.cssText =
          'background:#f4f4f5;color:#18181b;border:0;border-radius:0.375rem;padding:0.375rem 0.875rem;font-size:0.875rem;font-weight:500;cursor:pointer';
        actions.appendChild(retryBtn);
      }
    } else if (retryBtn !== null) {
      retryBtn.remove();
      retryBtn = null;
    }
  };

  update(initial);
  panel.appendChild(inner);
  root.appendChild(panel);

  return {
    update,
    cleanup() {
      panel.remove();
    },
  };
}
